import { useEffect, useRef, useState } from "react";
import { closeOnBackdrop } from "./dialog";
import { Icon } from "./Icon";

export interface ConfirmOptions {
  /** The question, e.g. "Delete main.go?" */
  title: string;
  /** What happens, e.g. "It's deleted for everyone in the room." */
  detail?: string;
  /** The action's button, e.g. "Delete". */
  confirmLabel: string;
  /** Destructive: the button is red. */
  danger?: boolean;
}

type Pending = ConfirmOptions & { resolve: (confirmed: boolean) => void };
let show: ((request: Pending) => void) | undefined;

/** Asks in an in-app dialog (like VS Code's), not the browser's confirm(). */
export function confirmAction(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (show) show({ ...options, resolve });
    else resolve(window.confirm(options.title)); // no app mounted (tests of parts in isolation)
  });
}

/** Where confirmAction's dialogs appear; mounted once by the app. */
export function ConfirmHost() {
  const [pending, setPending] = useState<Pending>();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const answered = useRef(false);

  useEffect(() => {
    show = (request) => {
      answered.current = false;
      setPending(request);
    };
    return () => {
      show = undefined;
    };
  }, []);

  useEffect(() => {
    if (!pending) return;
    dialogRef.current?.showModal();
    confirmRef.current?.focus();
  }, [pending]);

  const finish = (confirmed: boolean) => {
    if (!pending || answered.current) return;
    answered.current = true;
    pending.resolve(confirmed);
    dialogRef.current?.close();
    setPending(undefined);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes dialogs natively; this only adds backdrop clicks for the mouse
    <dialog
      ref={dialogRef}
      className="dialog confirm-dialog"
      aria-label={pending?.title ?? "Confirm"}
      onClick={closeOnBackdrop}
      onClose={() => finish(false)}
    >
      {pending && (
        <form
          method="dialog"
          onSubmit={(event) => {
            event.preventDefault();
            finish(true);
          }}
        >
          <div className="confirm-body">
            <span className={`confirm-icon${pending.danger ? " danger" : ""}`}>
              <Icon name="warning" size={22} />
            </span>
            <div>
              <h2>{pending.title}</h2>
              {pending.detail && <p className="dialog-hint">{pending.detail}</p>}
            </div>
          </div>
          <div className="dialog-actions">
            <button type="button" onClick={() => finish(false)}>
              Cancel
            </button>
            <button
              ref={confirmRef}
              type="submit"
              className={pending.danger ? "primary danger-primary" : "primary"}
            >
              {pending.confirmLabel}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
